import { useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import { parsePartnerDetails, parseContributingCenters, buildCrossProgramMatrix } from '../../../utils/dataProcessor';
import { parseCountryList } from '../../../utils/dataProcessor';
import { parseActorsDevUse, parseInnoUseActors, parseIPSRActors, parseIPSRCoreInfo, parseIPSRComplementaryInfo, parseIPSROrganizations } from '../../../utils/dataProcessor';
import { getISO3, isGlobalSouthCountry } from '../../../utils/countryMapping';
import { PROGRAM_MAPPING, IMPACT_SCORES } from '../../../utils/dataProcessor/constants';
import { filterResults } from '../../../utils/dataProcessor/filters';

const INNOVATION_TYPES = ['Innovation development', 'Innovation use', 'Innovation Package'];

export const PREDEFINED_DISCONTINUATION_REASONS = {
  'Limited Initiative resource availability required deprioritization of the innovation.':
    'Resource constraints (Initiative)',
  'No or limited progress in improving the readiness of the innovation.':
    'Limited readiness progress',
  'The innovation lead and/or team took up new responsibilities.':
    'Team changes / new responsibilities',
  'Limited bilateral co-investment required deprioritization of the innovation.':
    'Limited bilateral co-investment',
  'Absence of strong demand and scaling partners for the innovation.':
    'Weak demand / scaling partners',
  'The innovation achieved the target':
    'Target achieved',
};

/**
 * Shared data hook for all Innovation Analytics charts.
 * Filters the global results to innovation-type results only,
 * then derives all analytics needed by the 11 feasible charts.
 */
export function useInnovationData() {
  const { stats, filteredResults, resultsData, reportType, filters } = useApp();

  // Core: filter to innovation results only
  const innovationResults = useMemo(
    () => (filteredResults || []).filter(r => INNOVATION_TYPES.includes(r.type)),
    [filteredResults]
  );

  // Req 1: Innovation type breakdown (Dev / Use / Packages)
  const typeBreakdown = useMemo(() => {
    const counts = {};
    INNOVATION_TYPES.forEach(t => { counts[t] = 0; });
    innovationResults.forEach(r => { counts[r.type] = (counts[r.type] || 0) + 1; });
    return counts;
  }, [innovationResults]);

  // Req 2: Innovation Use Level distribution (1-9)
  const useLevelDistribution = useMemo(() => {
    const dist = {};
    innovationResults.forEach(r => {
      const level = r.innovationUseLevel;
      if (level && level >= 1 && level <= 9) {
        dist[level] = (dist[level] || 0) + 1;
      }
    });
    return dist;
  }, [innovationResults]);

  // Req 3: Readiness Level distribution (1-9)
  const readinessDistribution = useMemo(() => {
    const dist = {};
    innovationResults.forEach(r => {
      const level = r.readinessLevel;
      if (level && level >= 1 && level <= 9) {
        dist[level] = (dist[level] || 0) + 1;
      }
    });
    return dist;
  }, [innovationResults]);

  // Parse linkedResults strings into structured arrays (needed by linkage + evolution)
  const resultsWithParsedLinks = useMemo(() => {
    return innovationResults.map(r => {
      if (r.linkedResultsParsed) return r;
      if (!r.linkedResults) return r;
      const parsed = [];
      const re = /\((\d+):\s*([^-]+?)\s*-\s*([^)]+)\)/g;
      let m;
      while ((m = re.exec(r.linkedResults)) !== null) {
        parsed.push({ id: m[1], type: m[2].trim(), title: m[3].trim() });
      }
      return { ...r, linkedResultsParsed: parsed };
    });
  }, [innovationResults]);

  // Req 4: Linked results / bundles — Dev→Use→Package chains
  const linkageData = useMemo(() => {
    const withLinks = resultsWithParsedLinks.filter(r => r.linkedResultsParsed && r.linkedResultsParsed.length > 0);
    const resultsWithLinks = withLinks.length;
    let totalLinks = 0;
    const chainTypes = {};

    const shortTypeName = (t) => {
      if (t === 'Innovation development') return 'Dev';
      if (t === 'Innovation use') return 'Use';
      if (t === 'Innovation Package') return 'Package';
      return t || 'Unknown';
    };

    // Build lookups by result code for chain analysis and cross-program detection
    const idToType = new Map();
    const idToInitiative = new Map();
    resultsWithParsedLinks.forEach(r => {
      if (r.resultCode) {
        idToType.set(String(r.resultCode), r.type);
        idToInitiative.set(String(r.resultCode), r.initiative);
      }
    });

    // Count unique results with links by their actual type
    const sourceResultsByGroup = {};
    withLinks.forEach(r => {
      const group = shortTypeName(r.type);
      if (!sourceResultsByGroup[group]) sourceResultsByGroup[group] = new Set();
      sourceResultsByGroup[group].add(String(r.resultCode));
    });

    // Count links directionally (each link counted once from its source result)
    const crossProgramSet = new Set();

    withLinks.forEach(r => {
      const links = r.linkedResultsParsed || [];
      const sourceProg = r.initiative;
      links.forEach(link => {
        totalLinks++;

        // Classify chain type by actual direction (source → target)
        const sourceType = r.type;
        const targetType = link.type || idToType.get(String(link.id)) || 'Unknown';
        const label = `${shortTypeName(sourceType)}→${shortTypeName(targetType)}`;
        chainTypes[label] = (chainTypes[label] || 0) + 1;

        // Cross-program detection
        const linkedInitiative = idToInitiative.get(String(link.id));
        if (linkedInitiative && sourceProg && linkedInitiative !== sourceProg) {
          crossProgramSet.add(`${sourceProg}-${linkedInitiative}`);
        }
      });
    });

    const sourceResultCounts = {};
    Object.entries(sourceResultsByGroup).forEach(([group, codes]) => { sourceResultCounts[group] = codes.size; });
    return { resultsWithLinks, totalLinks, chainTypes, crossProgramLinks: crossProgramSet.size, sourceResultCounts };
  }, [resultsWithParsedLinks]);

  // Req 5: Internal collaboration — programs and centers on innovation results
  const internalCollaboration = useMemo(() => {
    const programContributions = {};
    const centerContributions = {};
    let multiProgramResults = 0;

    innovationResults.forEach(r => {
      // Count primary initiative
      if (r.initiative) {
        programContributions[r.initiative] = (programContributions[r.initiative] || 0) + 1;
      }
      // Count contributing initiatives
      const contribs = r.contributingInitiatives || [];
      if (contribs.length > 0) multiProgramResults++;
      contribs.forEach(init => {
        programContributions[init] = (programContributions[init] || 0) + 1;
      });

      // Centers
      const centers = r.centersParsed || [];
      centers.forEach(c => {
        if (c.code) centerContributions[c.code] = (centerContributions[c.code] || 0) + 1;
      });
      // Fallback: parse from raw text
      if (centers.length === 0 && r.contributingCenters) {
        const entries = r.contributingCenters.split(/\s*\/n\s*/).map(s => s.trim()).filter(Boolean);
        entries.forEach(entry => {
          const cleaned = entry.replace(/^\(|\)$/g, '').replace(/^Primary:\s*/i, '').trim();
          const dashIdx = cleaned.indexOf(' - ');
          const code = dashIdx > -1 ? cleaned.substring(0, dashIdx).trim() : cleaned;
          if (code) centerContributions[code] = (centerContributions[code] || 0) + 1;
        });
      }
    });

    return {
      programContributions,
      centerContributions,
      multiProgramResults,
      totalInnovationResults: innovationResults.length,
    };
  }, [innovationResults]);

  // Req 6: External partner collaboration on innovations
  const partnerData = useMemo(() => {
    const partners = parsePartnerDetails(innovationResults);
    const byDeliveryType = { Innovation: 0, Scaling: 0, Demand: 0, Other: 0 };
    let totalPartnerEntries = 0;

    partners.forEach(p => {
      totalPartnerEntries++;
      const types = p.deliveryTypes || [];
      if (types.length === 0) {
        byDeliveryType.Other++;
      } else {
        types.forEach(dt => {
          if (dt.includes('Innovation')) byDeliveryType.Innovation++;
          else if (dt.includes('Scaling')) byDeliveryType.Scaling++;
          else if (dt.includes('Demand')) byDeliveryType.Demand++;
          else byDeliveryType.Other++;
        });
      }
    });

    const topPartners = [...partners.values()]
      .sort((a, b) => b.resultCount - a.resultCount)
      .slice(0, 15);

    return { partners, topPartners, byDeliveryType, totalPartnerEntries };
  }, [innovationResults]);

  // Req 7: Innovation by geography
  const geographyData = useMemo(() => {
    const countryCounts = {};
    const regionCounts = {};

    const globalSouthOnly = filters?.geographicFocus === 'globalSouth';
    innovationResults.forEach(r => {
      const countries = r.countriesParsed || parseCountryList(r.countries || '');
      countries.forEach(c => {
        const name = typeof c === 'string' ? c : c;
        if (globalSouthOnly && !isGlobalSouthCountry(name)) return;
        countryCounts[name] = (countryCounts[name] || 0) + 1;
      });

      const regions = r.regionsParsed || (r.regions ? r.regions.split(/\r?\n-/).map(s => s.trim()).filter(Boolean) : []);
      regions.forEach(reg => {
        regionCounts[reg] = (regionCounts[reg] || 0) + 1;
      });
    });

    const topCountries = Object.entries(countryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);

    const topRegions = Object.entries(regionCounts)
      .sort((a, b) => b[1] - a[1]);

    // Build ISO3 map for map chart
    const iso3Counts = {};
    Object.entries(countryCounts).forEach(([country, count]) => {
      const iso3 = getISO3(country);
      if (iso3) {
        iso3Counts[iso3] = (iso3Counts[iso3] || 0) + count;
      }
    });

    return { countryCounts, regionCounts, topCountries, topRegions, iso3Counts };
  }, [innovationResults, filters?.geographicFocus]);

  // Req 8: Innovation by impact area
  const impactAreaData = useMemo(() => {
    const areas = ['gender', 'climate', 'nutrition', 'biodiversity', 'poverty'];
    const areaLabels = {
      gender: 'Gender Equality',
      climate: 'Climate Adaptation',
      nutrition: 'Nutrition & Food Security',
      biodiversity: 'Biodiversity',
      poverty: 'Poverty Reduction',
    };
    const tagFields = {
      gender: 'genderLevel',
      climate: 'climateLevel',
      nutrition: 'nutritionLevel',
      biodiversity: 'biodiversityLevel',
      poverty: 'povertyLevel',
    };

    const result = {};
    areas.forEach(area => {
      const field = tagFields[area];
      const counts = { Principal: 0, Significant: 0, 'Not Targeted': 0 };
      innovationResults.forEach(r => {
        const val = r[field] || 'Not Targeted';
        if (val.includes('Principal') || val === '(2) Principal') counts.Principal++;
        else if (val.includes('Significant') || val === '(1) Significant') counts.Significant++;
        else counts['Not Targeted']++;
      });
      result[area] = { label: areaLabels[area], ...counts, total: innovationResults.length };
    });

    return result;
  }, [innovationResults]);

  // Req 9: Innovation Development → Use evolution
  const devToUseEvolution = useMemo(() => {
    const devResults = resultsWithParsedLinks.filter(r => r.type === 'Innovation development');
    const useResults = resultsWithParsedLinks.filter(r => r.type === 'Innovation use');

    // Dev results that link to Use results
    const devWithUseLinks = devResults.filter(r => {
      const links = r.linkedResultsParsed || [];
      return links.some(link => link.type === 'Innovation use');
    });

    // Use results that link back to Dev results
    const useWithDevLinks = useResults.filter(r => {
      const links = r.linkedResultsParsed || [];
      return links.some(link => link.type === 'Innovation development');
    });

    // Readiness level distribution for Dev results with and without Use links
    const devWithUseLevels = {};
    const devWithoutUseLevels = {};
    devResults.forEach(r => {
      const level = r.readinessLevel;
      if (!level) return;
      const hasUseLink = (r.linkedResultsParsed || []).some(l => l.type === 'Innovation use');
      if (hasUseLink) {
        devWithUseLevels[level] = (devWithUseLevels[level] || 0) + 1;
      } else {
        devWithoutUseLevels[level] = (devWithoutUseLevels[level] || 0) + 1;
      }
    });

    // Total Dev→Use link pairs (before level filtering)
    let totalDevUseLinks = 0;
    devWithUseLinks.forEach(r => {
      totalDevUseLinks += (r.linkedResultsParsed || []).filter(l => l.type === 'Innovation use').length;
    });

    // Sankey flows: Readiness Level → Use Level for linked Dev→Use pairs
    const resultByCode = new Map();
    resultsWithParsedLinks.forEach(r => {
      if (r.resultCode) resultByCode.set(String(r.resultCode), r);
    });
    const flowCounts = {};
    const detailedLinks = [];
    devWithUseLinks.forEach(r => {
      const readiness = r.readinessLevel;
      if (!readiness) return;
      const useLinks = (r.linkedResultsParsed || []).filter(l => l.type === 'Innovation use');
      useLinks.forEach(link => {
        const useResult = resultByCode.get(String(link.id));
        const useLevel = useResult?.innovationUseLevel;
        if (!useLevel) return;
        const key = `${readiness}-${useLevel}`;
        flowCounts[key] = (flowCounts[key] || 0) + 1;
        detailedLinks.push({
          devCode: r.resultCode,
          devTitle: r.title,
          readiness,
          useCode: link.id,
          useTitle: useResult?.title || link.title,
          useLevel,
        });
      });
    });
    const sankeyFlows = Object.entries(flowCounts).map(([key, count]) => {
      const [readiness, use] = key.split('-').map(Number);
      return { readiness, use, count };
    });

    return {
      totalDev: devResults.length,
      totalUse: useResults.length,
      devWithUseLinks: devWithUseLinks.length,
      useWithDevLinks: useWithDevLinks.length,
      conversionRate: devResults.length > 0 ? (devWithUseLinks.length / devResults.length * 100).toFixed(1) : '0',
      devWithUseLevels,
      devWithoutUseLevels,
      sankeyFlows,
      detailedLinks,
      totalDevUseLinks,
    };
  }, [resultsWithParsedLinks]);

  // Req 10: Discontinued innovations
  // Bypass the global status filter — discontinued results are excluded by the default
  // status filter ['Approved', 'Quality Assessed'], so we re-filter from raw data.
  const discontinuedData = useMemo(() => {
    const statusUnfiltered = filterResults(resultsData || [], { ...filters, status: [] });
    const allInnovations = statusUnfiltered.filter(r => INNOVATION_TYPES.includes(r.type));
    const discontinued = allInnovations.filter(r =>
      r.status === 'Discontinued' || r.status === 'discontinued'
    );

    const byType = {};
    const byReadinessLevel = {};
    const byReason = {};
    discontinued.forEach(r => {
      byType[r.type] = (byType[r.type] || 0) + 1;
      if (r.readinessLevel) {
        byReadinessLevel[r.readinessLevel] = (byReadinessLevel[r.readinessLevel] || 0) + 1;
      }
      const raw = r.discontinuedReason;
      if (!raw) return;
      const segments = String(raw).split(';').map(s => s.trim()).filter(Boolean);
      segments.forEach(segment => {
        let matched = false;
        for (const [fullText, shortLabel] of Object.entries(PREDEFINED_DISCONTINUATION_REASONS)) {
          if (segment === fullText) {
            if (!byReason[shortLabel]) byReason[shortLabel] = { count: 0, fullText };
            byReason[shortLabel].count++;
            matched = true;
            break;
          }
        }
        if (!matched) {
          if (!byReason['Other']) byReason['Other'] = { count: 0, fullText: 'Other / free-text reasons' };
          byReason['Other'].count++;
        }
      });
    });

    return {
      total: discontinued.length,
      totalInnovations: allInnovations.length,
      byType,
      byReadinessLevel,
      byReason,
      rate: allInnovations.length > 0 ? (discontinued.length / allInnovations.length * 100).toFixed(1) : '0',
      results: discontinued,
    };
  }, [resultsData, filters]);

  // Req 11: Cross-program innovation co-contributions
  const crossProgramData = useMemo(
    () => buildCrossProgramMatrix(innovationResults),
    [innovationResults]
  );

  // NEW: Innovation Nature & Typology
  const natureTypologyData = useMemo(() => {
    const devResults = innovationResults.filter(r => r.type === 'Innovation development');
    if (devResults.length === 0) return null;

    const characterizationCounts = {};
    const typologyCounts = {};
    let varietyBreedCount = 0;
    let plannedCount = 0;
    let unplannedCount = 0;

    devResults.forEach(r => {
      const char = r.innovationCharacterization;
      if (char) characterizationCounts[char] = (characterizationCounts[char] || 0) + 1;

      const typ = r.innovationTypology;
      if (typ) typologyCounts[typ] = (typologyCounts[typ] || 0) + 1;

      if (r.isVarietyBreed === 'Yes' || r.isVarietyBreed === true) varietyBreedCount++;
      if (r.isPlanned === 'Yes') plannedCount++;
      else if (r.isPlanned === 'No') unplannedCount++;
    });

    return {
      total: devResults.length,
      characterizationCounts,
      typologyCounts,
      varietyBreedCount,
      plannedCount,
      unplannedCount,
    };
  }, [innovationResults]);

  // Req 16: Actor reach data — from Inno Use Actors, Actors Dev/Use, and IPSR Actors columns
  const userBreakdownData = useMemo(() => {
    const actorAgg = {};
    let grandTotal = 0, grandWomen = 0, grandMen = 0, grandYouth = 0;
    let resultsWithActorData = 0;
    let resultsWithGenderData = 0;

    innovationResults.forEach(r => {
      // Parse all three actor columns
      let actors = parseInnoUseActors(r.innoUseActors);
      if (actors.length === 0) {
        actors = parseActorsDevUse(r.actorsDevUse);
      }
      const ipsrActors = parseIPSRActors(r.ipsrActors);
      if (actors.length === 0 && ipsrActors.length === 0) return;
      resultsWithActorData++;

      // Merge per-row: deduplicate by actorType across columns
      // IPSR (col 86) often duplicates col 73 actor types with the same "How Many"
      // but adds Women/Men data. Merge by taking max total, prefer non-zero gender.
      const rowMap = {};
      for (const a of actors) {
        rowMap[a.actorType] = { ...a };
      }
      for (const a of ipsrActors) {
        const existing = rowMap[a.actorType];
        if (existing) {
          existing.total = Math.max(existing.total, a.total);
          if (a.women > 0 || a.men > 0) {
            existing.women = a.women;
            existing.men = a.men;
            existing.womenYouth = a.womenYouth;
            existing.menYouth = a.menYouth;
          }
        } else {
          rowMap[a.actorType] = { ...a };
        }
      }

      let rowHasGender = false;
      for (const a of Object.values(rowMap)) {
        if (!actorAgg[a.actorType]) actorAgg[a.actorType] = { total: 0, women: 0, men: 0, youth: 0 };
        actorAgg[a.actorType].total += a.total;
        actorAgg[a.actorType].women += a.women;
        actorAgg[a.actorType].men += a.men;
        actorAgg[a.actorType].youth += (a.womenYouth || 0) + (a.menYouth || 0);
        grandTotal += a.total;
        grandWomen += a.women;
        grandMen += a.men;
        grandYouth += (a.womenYouth || 0) + (a.menYouth || 0);
        if (a.women > 0 || a.men > 0) rowHasGender = true;
      }
      if (rowHasGender) resultsWithGenderData++;
    });

    const byActorType = Object.entries(actorAgg)
      .map(([type, data]) => ({ actorType: type, ...data }))
      .sort((a, b) => b.total - a.total);

    return { byActorType, grandTotal, grandWomen, grandMen, grandYouth, resultsWithActorData, resultsWithGenderData };
  }, [innovationResults]);

  // Req 15: Innovation Package analysis
  const packageAnalysisData = useMemo(() => {
    const pkgResults = innovationResults.filter(r => r.type === 'Innovation Package');
    if (pkgResults.length === 0) return null;

    const coreReadinessDist = {};
    const coreUseDist = {};
    let withComplementary = 0;
    let totalComplementary = 0;
    const orgTypeCounts = {};
    let totalHectares = 0;

    pkgResults.forEach(r => {
      const core = parseIPSRCoreInfo(r.ipsrCoreInfo);
      if (core) {
        if (core.readinessLevel) coreReadinessDist[core.readinessLevel] = (coreReadinessDist[core.readinessLevel] || 0) + 1;
        if (core.useLevel) coreUseDist[core.useLevel] = (coreUseDist[core.useLevel] || 0) + 1;
      }

      const comp = parseIPSRComplementaryInfo(r.ipsrComplementaryInfo);
      if (comp.length > 0) {
        withComplementary++;
        totalComplementary += comp.length;
      }

      const orgs = parseIPSROrganizations(r.ipsrOrganizations);
      orgs.forEach(o => {
        orgTypeCounts[o.orgType] = (orgTypeCounts[o.orgType] || 0) + o.count;
      });

      if (r.ipsrMeasures) {
        const haMatch = String(r.ipsrMeasures).match(/([\d,.]+)\s*(?:ha|hectares)/i);
        if (haMatch) totalHectares += parseFloat(haMatch[1].replace(/,/g, '')) || 0;
      }
    });

    const avgComplementary = withComplementary > 0 ? (totalComplementary / withComplementary).toFixed(1) : '0';

    return {
      totalPackages: pkgResults.length,
      withComplementary,
      totalComplementary,
      avgComplementary,
      coreReadinessDist,
      coreUseDist,
      orgTypeCounts,
      totalHectares,
      orgTypeCount: Object.keys(orgTypeCounts).length,
    };
  }, [innovationResults]);

  // Req 17: Package enabler bottlenecks
  const bottleneckData = useMemo(() => {
    const pkgResults = innovationResults.filter(r => r.type === 'Innovation Package');
    const allComplementary = [];

    pkgResults.forEach(r => {
      const comp = parseIPSRComplementaryInfo(r.ipsrComplementaryInfo);
      allComplementary.push(...comp);
    });

    if (allComplementary.length === 0) return null;

    const THRESHOLD = 5;
    const readinessDist = {};
    let bottleneckCount = 0;

    allComplementary.forEach(c => {
      if (c.readinessLevel != null) {
        readinessDist[c.readinessLevel] = (readinessDist[c.readinessLevel] || 0) + 1;
        if (c.readinessLevel < THRESHOLD) bottleneckCount++;
      }
    });

    const totalWithReadiness = Object.values(readinessDist).reduce((s, v) => s + v, 0);
    const bottleneckRate = totalWithReadiness > 0 ? ((bottleneckCount / totalWithReadiness) * 100).toFixed(1) : '0';

    return {
      totalComplementary: allComplementary.length,
      bottleneckCount,
      bottleneckRate,
      readinessDist,
      threshold: THRESHOLD,
    };
  }, [innovationResults]);

  return {
    // Core
    innovationResults,
    totalCount: innovationResults.length,
    reportType,

    // Per-requirement data
    typeBreakdown,                  // Req 1
    useLevelDistribution,           // Req 2
    readinessDistribution,          // Req 3
    linkageData,                    // Req 4
    internalCollaboration,          // Req 5
    partnerData,                    // Req 6
    geographyData,                  // Req 7
    impactAreaData,                 // Req 8
    devToUseEvolution,              // Req 9
    discontinuedData,               // Req 10
    crossProgramData,               // Req 11
    natureTypologyData,             // NEW: Nature & Typology
    userBreakdownData,              // Req 16
    packageAnalysisData,            // Req 15
    bottleneckData,                 // Req 17
  };
}
